import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  ADD_STOCK_TRANSACTION,
  EDIT_STOCK_TRANSACTION,
} from '../graphql/mutations/stockTransactions';
import { GET_STOCK_LOCATION_BALANCES } from '../graphql/queries/stockLocations';
import useStockLocations from '../hooks/useStockLocations';
import { formatAmount, normalizeDecimalInput, parseFloatFromString, subtractDecimalStrings } from '../utils/formatters';
import { SUPPORTED_CURRENCIES } from '../utils/currencies';
import { TransactionType } from '../utils/enums';
import TokenLocationManageModal from './TokenLocationManageModal';
import StockFxSnapshotFields from './StockFxSnapshotFields';
import { stockFxSnapshotChanges, stockFxSnapshotState, updateStockFxSnapshot } from '../utils/stockFxSnapshot';

const TYPES = [
  { label: 'Buy', value: TransactionType.BUY },
  { label: 'Sell', value: TransactionType.SELL },
  { label: 'Transfer In', value: TransactionType.TRANSFER_IN },
  { label: 'Transfer Out', value: TransactionType.TRANSFER_OUT },
  { label: 'Internal', value: TransactionType.INTERNAL_TRANSFER },
  { label: 'Split', value: TransactionType.SPLIT },
];

// Browse-only instruments carry a non-UUID identifier until their first transaction imports them.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const emptyForm = (currency, locationId = '') => ({
  ...stockFxSnapshotState(),
  transactionType: TransactionType.BUY,
  price: '',
  quantity: '',
  total: '',
  timestamp: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
  fees: '',
  notes: '',
  currency: currency || 'USD',
  locationId,
  sourceLocationId: '',
  destinationLocationId: '',
});

const friendlyError = (message) => {
  const raw = String(message || 'Could not save transaction');
  if (raw.toLowerCase().includes('insufficient holdings')) {
    return 'Not enough available shares for this transaction.';
  }
  return raw.split(':').pop().trim();
};

const StockTransactionModal = ({
  isOpen,
  onClose,
  stockId,
  portfolioId,
  stockCurrency,
  currentPrice,
  transaction,
  onSaved,
}) => {
  const isEditing = Boolean(transaction);
  const firstInputRef = useRef(null);
  const [form, setForm] = useState(emptyForm(stockCurrency));
  const [errors, setErrors] = useState({});
  const [showOptional, setShowOptional] = useState(false);
  const [addTransaction, { loading: adding }] = useMutation(ADD_STOCK_TRANSACTION);
  const [editTransaction, { loading: editing }] = useMutation(EDIT_STOCK_TRANSACTION);
  const loading = adding || editing;
  const [locationManagerField, setLocationManagerField] = useState(null);
  const locationTouchedRef = useRef(false);
  const {
    locations,
    createLocation,
    renameLocation,
    deleteLocation,
  } = useStockLocations(portfolioId, { skip: !isOpen });
  const { data: balancesData } = useQuery(GET_STOCK_LOCATION_BALANCES, {
    variables: { stockId, portfolioId },
    skip: !isOpen || !UUID_PATTERN.test(stockId || '') || !portfolioId,
    fetchPolicy: 'network-only',
  });
  const holdingsAmount = balancesData?.stockPosition?.holdingsAmount || '0';
  const locationBalances = useMemo(
    () => balancesData?.stockPosition?.locationBalances || [],
    [balancesData],
  );
  const balanceById = useMemo(() => {
    const byId = new Map();
    locationBalances.forEach((balance) => {
      const quantity = parseFloatFromString(balance.quantity);
      if (balance.locationId && quantity > 0) byId.set(balance.locationId, quantity);
    });
    return byId;
  }, [locationBalances]);
  const unassignedQuantity = parseFloatFromString(
    subtractDecimalStrings(holdingsAmount, locationBalances.map((balance) => balance.quantity || '0')),
  );
  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => (
      (balanceById.get(b.id) || 0) - (balanceById.get(a.id) || 0) || a.name.localeCompare(b.name)
    )),
    [locations, balanceById],
  );
  let largestBalanceLocationId = '';
  let largestBalance = 0;
  balanceById.forEach((quantity, locationId) => {
    if (quantity > largestBalance) {
      largestBalance = quantity;
      largestBalanceLocationId = locationId;
    }
  });
  const defaultLocationId = largestBalance > 0 && largestBalance >= unassignedQuantity ? largestBalanceLocationId : '';

  const isTransfer = TransactionType.isTransfer(form.transactionType);
  const isSplit = form.transactionType === TransactionType.SPLIT;
  const isInternalTransfer = form.transactionType === TransactionType.INTERNAL_TRANSFER;
  const isSellOrTransferOut = form.transactionType === TransactionType.SELL
    || form.transactionType === TransactionType.TRANSFER_OUT;
  const hasLocations = locations.length > 0;
  const valueLabel = form.transactionType === TransactionType.SELL ? 'Proceeds' : 'Cost';
  const currencies = [...new Set([
    stockCurrency,
    transaction?.currency,
    ...SUPPORTED_CURRENCIES,
  ].filter(Boolean))];

  useEffect(() => {
    if (!isOpen) return;
    locationTouchedRef.current = false;
    setLocationManagerField(null);
    if (transaction) {
      const price = transaction.price || '';
      const quantity = transaction.quantity || '';
      const numericTotal = parseFloatFromString(price) * parseFloatFromString(quantity);
      setForm({
        ...stockFxSnapshotState(transaction),
        transactionType: transaction.transactionType,
        price,
        quantity,
        total: numericTotal ? numericTotal.toString() : '',
        timestamp: format(new Date(transaction.timestamp), "yyyy-MM-dd'T'HH:mm"),
        fees: transaction.fees === '0' ? '' : transaction.fees || '',
        notes: transaction.notes || '',
        currency: transaction.currency || stockCurrency || 'USD',
        locationId: transaction.locationId || '',
        sourceLocationId: transaction.sourceLocationId || '',
        destinationLocationId: transaction.destinationLocationId || '',
      });
      setShowOptional(Boolean(transaction.fees !== '0' || transaction.notes));
    } else {
      setForm(emptyForm(stockCurrency));
      setShowOptional(false);
    }
    setErrors({});
    window.setTimeout(() => firstInputRef.current?.focus(), 25);
  }, [isOpen, transaction, stockCurrency]);

  // Balances load after the modal opens; preselect the main location until the user picks one.
  useEffect(() => {
    if (!isOpen || isEditing || locationTouchedRef.current || !defaultLocationId) return;
    setForm((current) => (
      current.transactionType === TransactionType.SPLIT || current.transactionType === TransactionType.INTERNAL_TRANSFER || current.locationId
        ? current
        : { ...current, locationId: defaultLocationId }
    ));
  }, [isOpen, isEditing, defaultLocationId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key !== 'Escape' || loading) return;
      if (locationManagerField) {
        setLocationManagerField(null);
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isOpen, loading, onClose, locationManagerField]);

  const selectType = (type) => {
    setForm((current) => {
      if (type === TransactionType.SPLIT) return {
        ...current, transactionType: type, price: '0', total: '', fees: '0',
        locationId: '', sourceLocationId: '', destinationLocationId: '',
      };
      const toInternal = type === TransactionType.INTERNAL_TRANSFER;
      const wasInternal = current.transactionType === TransactionType.INTERNAL_TRANSFER;
      if (toInternal === wasInternal) return { ...current, transactionType: type };
      return {
        ...current,
        transactionType: type,
        locationId: toInternal ? '' : (current.sourceLocationId || defaultLocationId),
        sourceLocationId: toInternal ? (current.locationId || largestBalanceLocationId) : '',
        destinationLocationId: '',
      };
    });
    setErrors({});
  };

  const setLocationField = (name, value) => {
    if (!name) return;
    locationTouchedRef.current = true;
    setForm((current) => ({ ...current, [name]: value || '' }));
    setErrors((current) => ({ ...current, [name]: null, submit: null }));
  };

  const handleManagedDelete = async (location) => {
    const deleted = await deleteLocation(location);
    if (deleted) {
      setForm((current) => ({
        ...current,
        locationId: current.locationId === location.id ? '' : current.locationId,
        sourceLocationId: current.sourceLocationId === location.id ? '' : current.sourceLocationId,
        destinationLocationId: current.destinationLocationId === location.id ? '' : current.destinationLocationId,
      }));
    }
    return deleted;
  };

  const locationOptionLabel = (location) => {
    const balance = balanceById.get(location.id);
    return balance ? `${location.name} (${formatAmount(balance)})` : location.name;
  };

  const setField = (name, value) => {
    setForm((current) => {
      const next = updateStockFxSnapshot(current, name, value);
      const price = parseFloatFromString(next.price);
      const quantity = parseFloatFromString(next.quantity);
      const total = parseFloatFromString(next.total);
      if ((name === 'price' || name === 'quantity') && price > 0 && quantity > 0) {
        next.total = (price * quantity).toString();
      } else if (name === 'total' && total > 0 && quantity > 0) {
        next.price = (total / quantity).toString();
      }
      return next;
    });
    setErrors((current) => ({ ...current, [name]: null, submit: null }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const quantity = parseFloatFromString(form.quantity);
    const price = parseFloatFromString(form.price);
    const nextErrors = {};
    if (!Number.isFinite(quantity) || !(quantity > 0)) nextErrors.quantity = 'Quantity must be greater than 0';
    if (isSplit && quantity === 1) nextErrors.quantity = 'Split ratio must differ from 1';
    if (!isTransfer && !isSplit && !(price > 0)) nextErrors.price = 'Price must be greater than 0';
    if (isInternalTransfer) {
      if (!form.sourceLocationId) nextErrors.sourceLocationId = 'Source location is required';
      if (!form.destinationLocationId) nextErrors.destinationLocationId = 'Destination location is required';
      if (form.sourceLocationId && form.sourceLocationId === form.destinationLocationId) {
        nextErrors.destinationLocationId = 'Destination must be different from source';
      }
    } else if (isSellOrTransferOut && hasLocations && !form.locationId && unassignedQuantity <= 0) {
      nextErrors.locationId = 'Select the location these shares leave from';
    }
    if (!portfolioId) nextErrors.submit = 'Select a portfolio first';
    if (!isEditing && !stockId) nextErrors.submit = 'Instrument identifier is missing';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    const variables = {
      ...stockFxSnapshotChanges(form, transaction || {}),
      portfolioId,
      transactionType: form.transactionType,
      price: isTransfer || isSplit ? '0' : normalizeDecimalInput(form.price),
      quantity: normalizeDecimalInput(form.quantity),
      timestamp: transaction && form.timestamp === format(new Date(transaction.timestamp), "yyyy-MM-dd'T'HH:mm")
        ? transaction.timestamp
        : new Date(form.timestamp).toISOString(),
      fees: !isSplit && form.fees ? normalizeDecimalInput(form.fees) : '0',
      feeQuantity: '0',
      notes: form.notes || '',
      currency: form.currency || stockCurrency || 'USD',
    };
    // Edits send empty strings so the backend clears fields that no longer apply.
    const locationVariables = isSplit
      ? { locationId: '', sourceLocationId: '', destinationLocationId: '' }
      : isInternalTransfer
      ? { locationId: '', sourceLocationId: form.sourceLocationId, destinationLocationId: form.destinationLocationId }
      : { locationId: form.locationId, sourceLocationId: '', destinationLocationId: '' };
    Object.entries(locationVariables).forEach(([key, value]) => {
      if (value || isEditing) variables[key] = value || '';
    });
    try {
      const response = isEditing
        ? await editTransaction({ variables: { ...variables, transactionId: transaction.id } })
        : await addTransaction({ variables: { ...variables, stockId } });
      const result = isEditing
        ? response.data?.editStockTransaction
        : response.data?.addStockTransaction;
      if (!result?.success) {
        setErrors({ submit: friendlyError(result?.error) });
        return;
      }
      toast.success(isEditing ? 'Transaction updated' : 'Transaction added');
      await onSaved?.(result);
      onClose();
    } catch (error) {
      setErrors({ submit: friendlyError(error.message) });
    }
  };

  if (!isOpen) return null;

  const inputClass = (hasError) => `w-full rounded border ${
    hasError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
  } bg-white px-3 py-2 text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`;

  const renderLocationLabel = (label, field) => (
    <div className="mb-2 flex items-center justify-between gap-2">
      <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{label}</span>
      <button
        type="button"
        onClick={() => setLocationManagerField(field)}
        disabled={loading || !portfolioId}
        className="text-xs font-medium text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-blue-400"
        aria-label={`Manage locations for ${label.toLowerCase()}`}
      >
        edit
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-gray-900/60 px-3 py-6">
      <div className="max-h-full w-full max-w-lg overflow-y-auto rounded-lg bg-white p-5 shadow-xl dark:bg-gray-800">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Stock Transaction' : 'Add Stock Transaction'}
          </h2>
          <button type="button" onClick={onClose} disabled={loading} className="text-gray-500 hover:text-gray-800 dark:text-gray-300">
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <TokenLocationManageModal
          isOpen={Boolean(locationManagerField)}
          onClose={() => setLocationManagerField(null)}
          locations={sortedLocations}
          selectedLocationId={locationManagerField ? form[locationManagerField] : ''}
          onSelect={(locationId) => setLocationField(locationManagerField, locationId)}
          onCreate={createLocation}
          onRename={renameLocation}
          onDelete={handleManagedDelete}
          tokenLocationBalances={locationBalances}
        />

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">Transaction Type</label>
            <div className="inline-flex max-w-full overflow-x-auto rounded-md border border-gray-300 dark:border-gray-600">
              {TYPES.map((type, index) => (
                <button
                  key={type.value}
                  ref={index === 0 ? firstInputRef : null}
                  type="button"
                  disabled={loading}
                  onClick={() => selectType(type.value)}
                  className={`whitespace-nowrap px-3 py-1.5 text-xs sm:text-sm ${
                    form.transactionType === type.value
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  } ${index ? 'border-l border-gray-300 dark:border-gray-600' : ''}`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {!isInternalTransfer && !isSplit && (
            <div className="mb-4">
              {renderLocationLabel('Location', 'locationId')}
              <select
                aria-label="Location"
                value={form.locationId}
                onChange={(event) => setLocationField('locationId', event.target.value)}
                className={inputClass(errors.locationId)}
                disabled={loading}
              >
                <option value="">Unassigned{unassignedQuantity > 0 ? ` (${formatAmount(unassignedQuantity)})` : ''}</option>
                {sortedLocations.map((location) => (
                  <option key={location.id} value={location.id}>{locationOptionLabel(location)}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {isSellOrTransferOut
                  ? 'Select the broker or account the shares leave from, unless they are unassigned.'
                  : 'Optional broker or account holding these shares.'}
              </p>
              {errors.locationId && <p className="mt-1 text-xs text-red-500">{errors.locationId}</p>}
            </div>
          )}

          {isInternalTransfer && (
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                ['sourceLocationId', 'From location', 'Select source'],
                ['destinationLocationId', 'To location', 'Select destination'],
              ].map(([field, label, placeholder]) => (
                <div key={field}>
                  {renderLocationLabel(label, field)}
                  <select
                    aria-label={label}
                    value={form[field]}
                    onChange={(event) => setLocationField(field, event.target.value)}
                    className={inputClass(errors[field])}
                    disabled={loading || !hasLocations}
                  >
                    <option value="">{placeholder}</option>
                    {sortedLocations.map((location) => (
                      <option key={location.id} value={location.id}>{locationOptionLabel(location)}</option>
                    ))}
                  </select>
                  {errors[field] && <p className="mt-1 text-xs text-red-500">{errors[field]}</p>}
                </div>
              ))}
              {!hasLocations && (
                <p className="text-xs text-gray-500 dark:text-gray-400 sm:col-span-2">
                  Create at least two locations with “edit”, then move shares between them.
                </p>
              )}
            </div>
          )}

          {!isTransfer && !isSplit && (
            <div className="mb-4">
              <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">{valueLabel}</label>
              <div className="flex gap-2">
                <input
                  value={form.total}
                  onChange={(event) => setField('total', event.target.value)}
                  inputMode="decimal"
                  className={inputClass(false)}
                  placeholder="0.00"
                  disabled={loading}
                />
                <select
                  value={form.currency}
                  onChange={(event) => setField('currency', event.target.value)}
                  className="w-24 rounded border border-gray-300 bg-white px-2 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  disabled={loading}
                >
                  {currencies.map((currency) => <option key={currency}>{currency}</option>)}
                </select>
              </div>
              <div className="mt-1 text-xs text-gray-500">Formula: {valueLabel} = Quantity × Price</div>
            </div>
          )}

          <div className="mb-4">
            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">{isSplit ? 'Split ratio (new shares per old share)' : 'Quantity'}</label>
            {isSplit && <p className="mb-2 text-xs text-gray-500">4 means 4-for-1; 0.1 means 1-for-10. Applies across all locations without changing cost basis.</p>}
            <input
              value={form.quantity}
              onChange={(event) => setField('quantity', event.target.value)}
              inputMode="decimal"
              className={inputClass(errors.quantity)}
              placeholder="0"
              disabled={loading}
            />
            {errors.quantity && <p className="mt-1 text-xs text-red-500">{errors.quantity}</p>}
          </div>

          {!isTransfer && !isSplit && (
            <div className="mb-4">
              <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">Price</label>
              <div className="relative">
                <input
                  value={form.price}
                  onChange={(event) => setField('price', event.target.value)}
                  inputMode="decimal"
                  className={`${inputClass(errors.price)} pr-20`}
                  placeholder="0.00"
                  disabled={loading}
                />
                {!form.price && Number(currentPrice) > 0 && (
                  <button
                    type="button"
                    onClick={() => setField('price', String(currentPrice))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    Current
                  </button>
                )}
              </div>
              {errors.price && <p className="mt-1 text-xs text-red-500">{errors.price}</p>}
            </div>
          )}

          <div className="mb-4">
            <label className="mb-2 block text-sm font-bold text-gray-700 dark:text-gray-300">Date and Time</label>
            <input
              type="datetime-local"
              value={form.timestamp}
              onChange={(event) => setField('timestamp', event.target.value)}
              className={inputClass(false)}
              disabled={loading}
            />
          </div>

          <button
            type="button"
            onClick={() => setShowOptional((value) => !value)}
            className="mb-3 flex w-full items-center text-sm font-medium text-gray-600 dark:text-gray-400"
          >
            Fees &amp; Notes (Optional)
            <span className="ml-auto">{showOptional ? '−' : '+'}</span>
          </button>
          <div className="mb-4">
            {!isSplit && <StockFxSnapshotFields values={form} original={transaction || {}} onChange={setField} disabled={loading} />}
          </div>
          {showOptional && (
            <div className="mb-4 space-y-3">
              <div>
                <label className="mb-1 block text-sm font-bold text-gray-700 dark:text-gray-300">Fees</label>
                <input
                  value={form.fees}
                  onChange={(event) => setField('fees', event.target.value)}
                  inputMode="decimal"
                  className={inputClass(false)}
                  placeholder="0.00"
                  disabled={loading || isSplit}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-bold text-gray-700 dark:text-gray-300">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(event) => setField('notes', event.target.value)}
                  className={inputClass(false)}
                  rows="2"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {errors.submit && <div className="mb-4 rounded bg-red-100 p-2 text-sm text-red-700">{errors.submit}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} disabled={loading} className="rounded bg-gray-200 px-4 py-2 font-semibold text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="rounded bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
              {loading ? 'Saving…' : isEditing ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockTransactionModal;
